import { useEffect, useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { api } from '../../lib/api'
import type { PaymentInfo } from '../../types'

// ─── UX Groups: 4 distinct payment experiences ────────

const UX_QR_CODE = ['pix', 'upi', 'paynow', 'duitnow', 'promptpay', 'nip_ng', 'instapay_ph', 'pse']
const UX_REDIRECT = ['sepa', 'ideal', 'faster_pay', 'interac', 'npp', 'spei', 'twint',
  'eft_za', 'mercadopago', 'webpay', 'paypay', 'bizum', 'bit_il', 'bancontact']
const UX_CODE_INPUT = ['blik', 'swish']
const UX_CARD = ['credit_card']

function getUxGroup(method: string): 'qr' | 'redirect' | 'code' | 'card' {
  if (UX_QR_CODE.includes(method)) return 'qr'
  if (UX_REDIRECT.includes(method)) return 'redirect'
  if (UX_CODE_INPUT.includes(method)) return 'code'
  return 'card'
}

// ─── Method display names ─────────────────────────────

const METHOD_NAMES: Record<string, string> = {
  pix: 'PIX', upi: 'UPI', sepa: 'SEPA', ideal: 'iDEAL', faster_pay: 'Open Banking',
  interac: 'Interac', npp: 'PayID', spei: 'SPEI', twint: 'TWINT', bizum: 'Bizum',
  paypay: 'PayPay', paynow: 'PayNow', swish: 'Swish', blik: 'BLIK',
  bancontact: 'Bancontact', promptpay: 'PromptPay', nip_ng: 'Bank Transfer',
  instapay_ph: 'InstaPay', eft_za: 'EFT', mercadopago: 'Mercado Pago',
  webpay: 'Webpay', duitnow: 'DuitNow', pse: 'PSE', bit_il: 'Bit',
  credit_card: 'Card',
}

// ─── Props ────────────────────────────────────────────

interface Props {
  signerData: {
    signer: { token: string }
    paymentConfig: {
      primary: string
      amount: number
      currency: string
      identityStrength: string
      ownAccountWarning: boolean
    }
  }
  onComplete: () => void
}

// ─── QR Code Payment (PIX, UPI, PayNow, DuitNow, PromptPay, etc.) ───

function QrCodePayment({ payment, method, t }: { payment: PaymentInfo; method: string; t: (key: string, fallback?: string) => string }) {
  const [copied, setCopied] = useState(false)
  const [timeLeft, setTimeLeft] = useState('')

  useEffect(() => {
    if (!payment.expiresAt) return
    const timer = setInterval(() => {
      const diff = new Date(payment.expiresAt!).getTime() - Date.now()
      if (diff <= 0) { setTimeLeft(t('sign.payment.expired', 'Expired')); clearInterval(timer); return }
      const m = Math.floor(diff / 60000)
      const s = Math.floor((diff % 60000) / 1000)
      setTimeLeft(`${m}:${String(s).padStart(2, '0')}`)
    }, 1000)
    return () => clearInterval(timer)
  }, [payment.expiresAt])

  function copyCode() {
    if (payment.copiaCola) {
      navigator.clipboard.writeText(payment.copiaCola)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className="space-y-4 text-center">
      <div className="text-lg font-semibold text-gray-900">{METHOD_NAMES[method] || method}</div>
      {payment.qrcodeData && (
        <div className="flex justify-center">
          <div className="bg-white p-4 rounded-xl border border-gray-200 inline-block">
            <img src={payment.qrcodeData} alt="QR Code" className="w-52 h-52 sm:w-56 sm:h-56 object-contain" />
          </div>
        </div>
      )}
      {timeLeft && (
        <div className="text-sm text-gray-500">
          {t('sign.step3.expires', 'Expires in')} <span className="font-mono font-semibold text-gray-900">{timeLeft}</span>
        </div>
      )}
      {payment.copiaCola && (
        <div>
          <p className="text-sm text-gray-500 mb-2">
            {t('sign.step3.paymentCode', 'Payment code')}
          </p>
          <div className="max-w-md mx-auto space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="text" readOnly value={payment.copiaCola}
                className="flex-1 px-3 py-2 text-xs bg-pakt-surface border border-pakt-line font-pakt-mono truncate"
              />
            </div>
            <button onClick={copyCode}
              className="w-full py-3 text-sm font-semibold bg-pakt-ink text-white hover:bg-pakt-ink-2 transition">
              {copied ? t('common.copied', 'Copied!') : t('common.copy', 'Copy code')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Redirect Payment (SEPA, iDEAL, Open Banking, etc.) ──

function RedirectPayment({ payment, method, t }: { payment: PaymentInfo; method: string; t: (key: string, fallback?: string) => string }) {
  return (
    <div className="space-y-4 text-center">
      <div className="text-lg font-semibold text-gray-900">{METHOD_NAMES[method] || method}</div>
      <p className="text-sm text-gray-500">
        {t('sign.step3.redirecting', 'You will be redirected to complete the payment.')}
      </p>
      <div className="bg-gray-50 rounded-lg p-4 max-w-md mx-auto text-sm text-gray-700">
        {t('sign.step3.amount', 'Amount')}: <span className="font-semibold">{payment.amount} {payment.currency}</span>
      </div>
      {payment.deeplink ? (
        <a href={payment.deeplink}
          className="inline-block px-8 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition">
          {t('sign.step3.payWith', 'Pay with')} {METHOD_NAMES[method] || method}
        </a>
      ) : (
        <p className="text-sm text-gray-400">{t('sign.step3.preparing', 'Preparing payment link...')}</p>
      )}
    </div>
  )
}

// ─── Code Input Payment (BLIK, Swish) ─────────────────

function CodeInputPayment({ payment, method, t }: { payment: PaymentInfo; method: string; t: (key: string, fallback?: string) => string }) {
  const isBlik = method === 'blik'

  return (
    <div className="space-y-4 text-center">
      <div className="text-lg font-semibold text-gray-900">{METHOD_NAMES[method] || method}</div>
      {isBlik ? (
        <>
          <p className="text-sm text-gray-500">
            {t('sign.step3.blikInstruction', 'Open your banking app, go to BLIK, and enter the 6-digit code.')}
          </p>
          <p className="text-xs text-amber-600">{t('sign.step3.blikExpires', 'Code expires in 2 minutes.')}</p>
        </>
      ) : (
        <p className="text-sm text-gray-500">
          {t('sign.step3.swishInstruction', 'Open the Swish app on your phone and confirm the payment.')}
        </p>
      )}
      <div className="bg-gray-50 rounded-lg p-4 max-w-md mx-auto text-sm text-gray-700">
        {t('sign.step3.amount', 'Amount')}: <span className="font-semibold">{payment.amount} {payment.currency}</span>
      </div>
    </div>
  )
}

// ─── Card Payment ─────────────────────────────────────

function CardPayment({
  payment, onSubmitCard, submitting, t
}: {
  payment: PaymentInfo
  onSubmitCard: (data: { number: string; expiry: string; cvc: string; name: string }) => void
  submitting: boolean
  t: (key: string, fallback?: string) => string
}) {
  const [card, setCard] = useState({ number: '', expiry: '', cvc: '', name: '' })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    onSubmitCard(card)
  }

  return (
    <div className="max-w-md mx-auto">
      <div className="text-lg font-semibold text-gray-900 text-center mb-4">{t('sign.step3.cardTitle', 'Card Payment')}</div>
      <div className="bg-gray-50 rounded-lg p-3 mb-4 text-center text-sm text-gray-600">
        {t('sign.step3.amount', 'Amount')}: <span className="font-semibold">{payment.amount} {payment.currency}</span>
      </div>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('sign.step3.cardholderName', 'Cardholder Name')}</label>
          <input type="text" required value={card.name}
            onChange={(e) => setCard(p => ({ ...p, name: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
            placeholder="Maria Silva" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('sign.step3.cardNumber', 'Card Number')}</label>
          <input type="text" required maxLength={19} value={card.number}
            onChange={(e) => setCard(p => ({ ...p, number: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-mono"
            placeholder="4242 4242 4242 4242" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('sign.step3.expiry', 'Expiry')}</label>
            <input type="text" required maxLength={5} value={card.expiry}
              onChange={(e) => setCard(p => ({ ...p, expiry: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-mono"
              placeholder="MM/YY" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('sign.step3.cvc', 'CVC')}</label>
            <input type="text" required maxLength={4} value={card.cvc}
              onChange={(e) => setCard(p => ({ ...p, cvc: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-mono"
              placeholder="123" />
          </div>
        </div>
        <button type="submit" disabled={submitting}
          className="w-full py-2.5 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition">
          {submitting ? t('sign.step3.processing', 'Processing...') : t('sign.step3.payNow', 'Pay Now')}
        </button>
      </form>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────

export default function Step3Payment({ signerData, onComplete }: Props) {
  const { t: tRaw } = useTranslation()
  const t = tRaw as unknown as (key: string, fallback?: string) => string
  const { token } = useParams<{ token: string }>()

  const [payment, setPayment] = useState<PaymentInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [submittingCard, setSubmittingCard] = useState(false)
  const [ownAccountConfirmed, setOwnAccountConfirmed] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { primary, amount, currency, ownAccountWarning } = signerData.paymentConfig
  const uxGroup = getUxGroup(primary)
  const methodName = METHOD_NAMES[primary] || primary

  const createPayment = useCallback(async () => {
    try {
      const res = await api.post<PaymentInfo>(`/api/sign/${token}/payment/create`)
      setPayment(res)
    } catch {
      setError(t('sign.step3.paymentFailed', 'Payment failed. Please check your details and try again.'))
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    createPayment()
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [createPayment])

  // Poll payment status
  useEffect(() => {
    if (!payment) return
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get<{ status: string }>(`/api/sign/${token}/payment/status`)
        if (res.status === 'paid') {
          if (pollRef.current) clearInterval(pollRef.current)
          onComplete()
        }
      } catch { /* continue polling */ }
    }, 5000)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [payment, token, onComplete])

  async function handleCardSubmit(cardData: { number: string; expiry: string; cvc: string; name: string }) {
    setSubmittingCard(true)
    setError('')
    try {
      await api.post(`/api/sign/${token}/payment/card`, cardData)
      const res = await api.get<{ status: string }>(`/api/sign/${token}/payment/status`)
      if (res.status === 'paid') onComplete()
    } catch {
      setError(t('sign.step3.paymentFailed', 'Payment failed. Please check your details and try again.'))
    } finally {
      setSubmittingCard(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4" />
        <p className="text-gray-500">{t('sign.step3.loading', 'Setting up payment...')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          {t('sign.step3.title', 'Verify your identity')}
        </h2>
        <p className="text-sm text-gray-500 mb-2">
          {t('sign.step3.desc', 'A small identity verification fee is required to complete signing.')}
        </p>

        {/* Amount display */}
        <div className="bg-pakt-surface border border-pakt-line p-4 mb-4 flex flex-col sm:flex-row items-center sm:justify-between gap-2">
          <div>
            <span className="text-2xl font-bold text-pakt-ink font-pakt-display">{currency} {amount}</span>
            <span className="text-sm text-indigo-400 ml-2">via {methodName}</span>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Own account warning */}
        {ownAccountWarning && (
          <div className="mb-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={ownAccountConfirmed}
                onChange={(e) => setOwnAccountConfirmed(e.target.checked)}
                className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
              <span className="text-sm text-gray-700">
                {t('sign.step3.ownAccount',
                  'I confirm that I am using my own payment method / account for this transaction.')}
              </span>
            </label>
          </div>
        )}

        {/* Payment method — UX group selection */}
        {payment && (ownAccountConfirmed || !ownAccountWarning) && (
          <div>
            {uxGroup === 'qr' && <QrCodePayment payment={payment} method={primary} t={t} />}
            {uxGroup === 'redirect' && <RedirectPayment payment={payment} method={primary} t={t} />}
            {uxGroup === 'code' && <CodeInputPayment payment={payment} method={primary} t={t} />}
            {uxGroup === 'card' && (
              <CardPayment payment={payment} onSubmitCard={handleCardSubmit} submitting={submittingCard} t={t} />
            )}

            {/* Polling indicator */}
            {uxGroup !== 'card' && (
              <div className="mt-6 text-center">
                <div className="inline-flex items-center gap-2 text-sm text-gray-500">
                  <div className="animate-pulse w-2 h-2 rounded-full bg-indigo-500" />
                  {t('sign.step3.waiting', 'Waiting for payment confirmation...')}
                </div>
              </div>
            )}
          </div>
        )}

        {ownAccountWarning && !ownAccountConfirmed && payment && (
          <p className="text-sm text-gray-400 text-center py-4">
            {t('sign.step3.confirmFirst', 'Please confirm the checkbox above to proceed.')}
          </p>
        )}
      </div>
    </div>
  )
}
